import { action, computed, observable } from 'mobx';

import { AppManager, Variables, Input } from 'core';

import { PaymentSpaceModel, PaymentSpaceApp } from 'apps/payment/app';
import { clientStore } from 'apps/client/app';

import {
  InitiativePreviewSubscriptionQuery,
  ProfileSubscriptionQuery,
  CreateSubscriptionMutation,
  CancelSubscriptionMutation,
  AddSubscriptionSeatsMutation,
  RemoveSubscriptionSeatsMutation,
  ProfileAlterSeatsPreviewQuery,
  OrganizationAlterSeatsPreviewQuery,
  ReactivateSubscriptionMutation,
  InitiativeStorageUsageQuery,
  ProfileStorageUsageQuery,
  ProfileConversionUsageQuery,
} from 'apps/payment/gql';
import { isProfileSlug } from 'utils';

export class SubscriptionAppManager extends AppManager {
  @observable addSeatsPreviewResult = null;
  @observable seatsUsage = null;
  @observable storageUsage = null;
  @observable conversionUsage = null;

  static registryName = 'SubscriptionAppManager';
  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('paymentSpace', PaymentSpaceApp);
    this.AppRef('paymentSpacePreview', PaymentSpaceApp);

    this.variables = new Variables({
      spaceSlug: () => this.spaceSlug,
    });

    this.previewVariables = new Variables({
      spaceSlug: () => this.spaceSlug,
      coupon: () => (this.paymentSpace ? this.paymentSpace.coupon : ''),
    });

    this.variables.onChange(['spaceSlug'], this.NewSubscriptionQuery);
  }

  @computed get loading() {
    const subscriptionQuery = this.queries.get('subscription');
    return subscriptionQuery ? subscriptionQuery.loading : true;
  }

  @computed get loadingPreview() {
    const previewQuery = this.queries.get('preview');
    return previewQuery ? previewQuery.loading : false;
  }

  @action NewSubscriptionQuery = (options = { preview: false }) => {
    const { preview } = options;
    if (!this.variables.spaceSlug) return null;
    let PreviewSubscriptionQuery = null;

    if (this.variables.spaceSlug.startsWith('+')) {
      PreviewSubscriptionQuery = InitiativePreviewSubscriptionQuery;
    } else if (this.variables.spaceSlug.startsWith('@')) {
      PreviewSubscriptionQuery = ProfileSubscriptionQuery;
    }
    if (!PreviewSubscriptionQuery) return null;

    const q = new PreviewSubscriptionQuery(this, {
      queryKey: preview ? 'preview' : 'subscription',
      fetchPolicy: 'network-only',
      variables: preview ? this.previewVariables : this.variables,
    });
    q.process(
      result => {
        if (result) {
          this.seatsUsage = result.seatsUsage;
        }

        clientStore.QuerySpaceForPlan(clientStore.slugFromRouteOrSpace);

        if (clientStore.isInProfileSpace) {
          const conversionUsageQuery = new ProfileConversionUsageQuery(this, {
            fetchPolicy: 'network-only',
            variables: { username: clientStore.signedInProfile.username },
            queryKey: 'profileConversionUsageInNewSubscription',
          });

          conversionUsageQuery.process(r => {
            if (r) {
              this.conversionUsage = r.conversionUsage;
            }
          });

          if (result?.billingSubscriptions) {
            const isSubscriptionCanceled =
              result.billingSubscriptions.length > 0 &&
              result.billingSubscriptions[
                result.billingSubscriptions.length - 1
              ].status === 'CANCELED';

            if (
              result.isEarlyAccess ||
              result.billingSubscriptions.length === 0 ||
              (clientStore.isInProfileSpace && isSubscriptionCanceled)
            ) {
              conversionUsageQuery.query();
            }
          }
        }

        if (result?.inSpace?.flags) {
          const flags = JSON.parse(result.inSpace.flags);

          const QueryStorage = isProfileSlug(this.spaceSlug)
            ? ProfileStorageUsageQuery
            : InitiativeStorageUsageQuery;

          const variables = isProfileSlug(this.spaceSlug)
            ? { username: this.spaceSlug }
            : { spaceSlug: this.spaceSlug };

          const storageQuery = new QueryStorage(this, {
            fetchPolicy: 'network-only',
            variables,
          });

          storageQuery.process(r => {
            if (r) {
              this.storageUsage = r.storageUsage;
            }
          });

          storageQuery.query();
        }

        return result ? PaymentSpaceModel.Load(result) : null;
      },
      preview ? this.UpdatePaymentSpacePreview : this.UpdatePaymentSpace,
    );
    q.query();
    return q;
  };

  @action UpdatePaymentSpace = paymentSpaceModel => {
    this.paymentSpace = paymentSpaceModel;
  };

  @action UpdatePaymentSpacePreview = paymentSpaceModel => {
    if (this.paymentSpacePreview && paymentSpaceModel) {
      this.paymentSpacePreview.setUpstream(paymentSpaceModel);
    } else if (paymentSpaceModel) {
      this.paymentSpacePreview = paymentSpaceModel;
    }
    if (this.paymentSpacePreview && !this.paymentSpacePreview.form) {
      this.paymentSpacePreview.InitVATForm();
    }
  };

  @action SubmitSubscription = (paymentSpace, options) => {
    const subscriptionInput = new Input({
      spaceSlug: () => paymentSpace.spaceSlug,
      countryCode: () => paymentSpace.countryCode,
      taxId: () => paymentSpace.taxId,
      coupon: () => paymentSpace.coupon,
      extraSeats: () => paymentSpace.extraSeats || 0,
    });
    const mutation = CreateSubscriptionMutation.New(subscriptionInput, options);
    return mutation;
  };

  @action CancelSubscription = (paymentSpace, options = {}) => {
    const input = new Input({
      subscriptionId: paymentSpace.subscription.id,
    });
    const mutation = CancelSubscriptionMutation.New(input, options);
    return mutation;
  };

  @action ReactivateSubscription = (paymentSpace, options = {}) => {
    const input = new Input({
      spaceId: clientStore.activeSpaceId,
    });
    const mutation = ReactivateSubscriptionMutation.New(input, options);
    return mutation;
  };

  @action NewPreviewExtraSeatsQuery = extraSeats => {
    const { spaceSlug, coupon } = this.variables;
    if (!spaceSlug) return null;
    let PreviewSubscriptionQuery = null;
    const variables = new Variables({ spaceSlug, coupon, extraSeats });
    if (spaceSlug.startsWith('+')) {
      PreviewSubscriptionQuery = InitiativePreviewSubscriptionQuery;
    } else if (spaceSlug.startsWith('@')) {
      PreviewSubscriptionQuery = ProfileSubscriptionQuery;
    }
    if (!PreviewSubscriptionQuery) return null;

    const q = new PreviewSubscriptionQuery(this, {
      queryKey: 'preview',
      fetchPolicy: 'no-cache',
      variables,
    });
    q.process(result => {
      if (result && result.subscriptionPreview) {
        return PaymentSpaceModel.Load(result);
      }
      return null;
    }, this.UpdatePaymentSpacePreview);
    q.query();
    return q;
  };

  @action NewPreviewAlterSeatsQuery = (addedSeats, removedSeats) => {
    this.paymentSpacePreview = null;
    const variables = new Variables({
      spaceSlug: () => this.variables.spaceSlug,
      addedSeats: () => addedSeats || 0,
      removedSeats: () => removedSeats || 0,
    });

    let PreviewQuery = null;
    if (this.spaceSlug.startsWith('+')) {
      PreviewQuery = OrganizationAlterSeatsPreviewQuery;
    } else if (this.spaceSlug.startsWith('@')) {
      PreviewQuery = ProfileAlterSeatsPreviewQuery;
    }
    if (!PreviewQuery) return null;
    const alterSeatsPreviewQuery = new PreviewQuery(this, {
      variables,
      queryKey: 'preview',
      fetchPolicy: 'no-cache',
    });
    alterSeatsPreviewQuery.process(result => {
      if (result?.billingSubscriptions) {
        this.addSeatsPreviewResult =
          result.billingSubscriptions[0].addSeatsPreview;
      }

      return result ? PaymentSpaceModel.Load(result) : null;
    }, this.UpdatePaymentSpacePreview);
    alterSeatsPreviewQuery.query();
    return alterSeatsPreviewQuery;
  };

  @action removeUnassignedSeats = (removeSeats, options = {}) => {
    const input = new Input({
      subscriptionId: this.paymentSpace.subscription.id,
      removeNSeats: removeSeats,
    });

    return RemoveSubscriptionSeatsMutation.New(input, options);
  };

  @action SaveAlterSeats = (seats, options = {}) => {
    let input = null;
    let AlterSubscriptionSeatsMutation = null;
    if (seats.addSeatsAmount) {
      input = new Input({
        subscriptionId: this.paymentSpace.subscription.id,
        addNSeats: seats.addSeatsAmount,
      });
      AlterSubscriptionSeatsMutation = AddSubscriptionSeatsMutation;
    } else if (seats.removeSeatsAmount) {
      input = new Input({
        subscriptionId: this.paymentSpace.subscription.id,
        removeNSeats: seats.removeSeatsAmount,
      });
      AlterSubscriptionSeatsMutation = RemoveSubscriptionSeatsMutation;
    }

    if (!(AlterSubscriptionSeatsMutation && input)) return null;
    const mutation = AlterSubscriptionSeatsMutation.New(input, options);
    return mutation;
  };
}
